// [matrah] Shared constants for Meta WhatsApp message templates.
// The five bot languages of the Zakat Matrah project, plus Meta's limits, so the
// template builder, the template library and the preview agree on one source.

export const TEMPLATE_LANGUAGES = [
  { code: 'ar', name: 'Arabic', native: 'العربية', dir: 'rtl', flag: 'om' },
  { code: 'en', name: 'English', native: 'English', dir: 'ltr', flag: 'gb' },
  { code: 'ur', name: 'Urdu', native: 'اردو', dir: 'rtl', flag: 'pk' },
  { code: 'bn', name: 'Bengali', native: 'বাংলা', dir: 'ltr', flag: 'bd' },
  { code: 'hi', name: 'Hindi', native: 'हिन्दी', dir: 'ltr', flag: 'in' },
]

export const TEMPLATE_CATEGORIES = [
  { value: 'UTILITY', title: 'Utility', hint: 'Transactional: payment links, reminders, confirmations' },
  { value: 'MARKETING', title: 'Marketing', hint: 'Promotional: campaigns, appeals, announcements' },
]

export const HEADER_TYPES = [
  { value: 'NONE', title: 'None', icon: 'mdi-minus-circle-outline' },
  { value: 'TEXT', title: 'Text', icon: 'mdi-format-title' },
  { value: 'IMAGE', title: 'Image', icon: 'mdi-image-outline' },
  { value: 'VIDEO', title: 'Video', icon: 'mdi-video-outline' },
  { value: 'DOCUMENT', title: 'Document', icon: 'mdi-file-document-outline' },
]

export const BUTTON_TYPES = [
  { value: 'QUICK_REPLY', title: 'Quick reply', icon: 'mdi-reply-outline' },
  { value: 'URL', title: 'Website URL', icon: 'mdi-open-in-new' },
  { value: 'PHONE_NUMBER', title: 'Call phone number', icon: 'mdi-phone-outline' },
]

// Meta limits (https://developers.facebook.com/docs/whatsapp/business-management-api/message-templates)
export const META_LIMITS = {
  nameMax: 512,
  headerTextMax: 60,
  bodyMax: 1024,
  footerMax: 60,
  buttonTextMax: 25,
  urlMax: 2000,
  phoneMax: 20,
  buttonsMax: 10,
  quickReplyMax: 3,
  urlMax_count: 2,
  phoneMax_count: 1,
}

export const STATUS_COLORS = {
  APPROVED: 'success',
  PENDING: 'warning',
  REJECTED: 'error',
  PAUSED: 'grey',
  DISABLED: 'grey',
  IN_APPEAL: 'info',
  PENDING_DELETION: 'grey',
  DELETED: 'grey',
}

/** Meta may return `en_US`, `en_GB`, `ar_EG` … — reduce to the bot language code. */
export function baseLanguageCode(code) {
  return String(code || '').split('_')[0].toLowerCase()
}

export function languageMeta(code) {
  const base = baseLanguageCode(code)
  return (
    TEMPLATE_LANGUAGES.find((l) => l.code === base) || {
      code: base || '??',
      name: code || 'Unknown',
      native: code || 'Unknown',
      dir: 'ltr',
      flag: null,
    }
  )
}

export function statusColor(status) {
  return STATUS_COLORS[status] || 'grey'
}

/** Meta template names: lowercase letters, digits and underscores only. */
export function slugifyTemplateName(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
    .replace(/_+/g, '_')
    .slice(0, META_LIMITS.nameMax)
}

/** Returns the distinct `{{n}}` indexes used in a text, in order of appearance. */
export function extractVariables(text) {
  const found = []
  const re = /\{\{\s*(\d+)\s*\}\}/g
  let match
  while ((match = re.exec(String(text || ''))) !== null) {
    const n = parseInt(match[1], 10)
    if (!found.includes(n)) found.push(n)
  }
  return found
}

function escapeHtml(text) {
  return String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * Renders WhatsApp markdown (*bold* _italic_ ~strike~) and `{{n}}` samples into
 * safe HTML for the live preview. Samples are highlighted so the admin sees
 * which text is dynamic.
 */
export function renderWhatsAppMarkup(text, samples = []) {
  let html = escapeHtml(text)
  html = html.replace(/\{\{\s*(\d+)\s*\}\}/g, (_, n) => {
    const idx = parseInt(n, 10) - 1
    const sample = samples[idx]
    const label = sample && String(sample).trim() !== '' ? escapeHtml(sample) : `{{${n}}}`
    return `<mark class="wa-var">${label}</mark>`
  })
  html = html.replace(/\*([^*\n]+)\*/g, '<strong>$1</strong>')
  html = html.replace(/(^|[^\w])_([^_\n]+)_(?!\w)/g, '$1<em>$2</em>')
  html = html.replace(/~([^~\n]+)~/g, '<s>$1</s>')
  html = html.replace(/\n/g, '<br>')
  return html
}
